import React, {useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Box, CircularProgress, Stack, TextField} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Icon,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import {SrtItem} from '@contentlab/utils/srtUtils';
import {
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import {raiseContentStudioError} from '@/utils/contentStudioErrorHandling';
import translate from '@/utils/translate';
import projectFetchers from '@/api/fetchers/content-studio/project';
import useContentStudioStore, {
  contentStudioActions,
  ErrorKind,
} from '@/stores/contentStudioStore';
import {calculateLength, calculatePhraseScore} from './util';

function SrtRowEditor({
  item,
  editRow,
  isEdited,
  restoreOriginal,
  currentIndex,
  setCurrentIndex,
}: {
  item: SrtItem;
  editRow: (newText: string, shouldCloseRow: boolean) => void;
  isEdited: boolean;
  restoreOriginal: () => void;
  currentIndex: string | null;
  setCurrentIndex: (index: string | null) => void;
}) {
  const {uiInteraction} = useUiInteractionEvent();
  const [newText, setNewText] = useState(item.currentText);
  const {phrase, color} = calculatePhraseScore(
    calculateLength(newText) - calculateLength(item.originalText)
  );
  const currentRephraseProject = useContentStudioStore(
    useShallow((state) => state.currentRephraseProject)
  );
  const [tts, setTts] = useState<Blob | null>(null);
  useEffect(() => {
    if (tts) {
      const audio = new Audio(URL.createObjectURL(tts));
      audio.play();
    }
  }, [tts]);
  const [isLoadingSound, setIsLoadingSound] = useState(false);

  const onSubmit = (shouldCloseRow = true) => {
    uiInteraction({
      item_name: 'rephrase_row_editor_update_script',
      interaction_location: CONTENT_STUDIO_VIEW_NAMES.REPHRASE_ROW_EDITOR,
      interaction_details: newText,
      flow_name: FLOW_NAMES.CONTENT_STUDIO.REPHRASE,
    });
    editRow(newText, shouldCloseRow);
  };

  return (
    <Stack
      bgcolor={designSystemToken('semantic.bg.alpha')}
      borderRadius="8px"
      padding="12px"
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Body
          size="md"
          color={designSystemToken('semantic.fg.inverse-secondary')}
        >
          {translate('original_length_and_time', {
            originalLength: calculateLength(item.originalText),
            originalTime: (item.endSeconds - item.startSeconds).toFixed(2),
          })}
        </Body>
        <Box
          marginTop="10px"
          sx={{cursor: 'pointer'}}
          onClick={() => {
            if (currentRephraseProject && !isLoadingSound) {
              setIsLoadingSound(true);
              projectFetchers
                .getTTSByProjectId(currentRephraseProject?.id, newText)
                .then((response) => {
                  setTts(response);
                })
                .catch(() => {
                  raiseContentStudioError(
                    'Failed to get TTS',
                    ErrorKind.FAILURE
                  );
                })
                .finally(() => {
                  setIsLoadingSound(false);
                });
            }
          }}
        >
          {isLoadingSound ? (
            <CircularProgress size={20} />
          ) : (
            <Icon name="Features-Audio-On" size="medium" appearance="primary" />
          )}
        </Box>
      </Stack>
      <TextField
        autoFocus
        value={newText}
        onChange={(e) => {
          setNewText(e.target.value);
          editRow(e.target.value, false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit(false);
            if (currentIndex !== null) {
              setCurrentIndex(String(Number.parseInt(currentIndex, 10) + 1));
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onSubmit(true);
          }
        }}
        inputProps={{
          spellCheck: false,
          autoCapitalize: 'off',
          autoCorrect: 'off',
        }}
        sx={{
          'backgroundColor': designSystemToken('semantic.bg.secondary'),
          'input': {
            fontSize: '18px',
            color: designSystemToken('semantic.fg.primary'),
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: `1px solid ${designSystemToken(
                'semantic.bg.neutral-inverse'
              )}`,
            },
            '&:hover fieldset': {
              border: `1px solid ${designSystemToken(
                'semantic.bg.neutral-inverse'
              )}`,
            },
            '&.Mui-focused fieldset': {
              border: `1px solid ${designSystemToken(
                'semantic.bg.neutral-inverse'
              )}`,
            },
          },
          'borderRadius': '8px',
        }}
      />
      <Stack direction="row" alignItems="center" marginTop="10px">
        {isEdited && (
          <>
            <Body
              size="md"
              color={designSystemToken('semantic.fg.inverse-secondary')}
            >
              {translate('new_length', {
                newLength: calculateLength(newText),
              })}
            </Body>
            <Body size="md" color={color}>
              &nbsp;{phrase}
            </Body>
          </>
        )}
        <Button
          appearance="neutral"
          mode="plain"
          size="small"
          disabled={!isEdited}
          sx={{
            marginLeft: 'auto',
          }}
          onClick={() => {
            restoreOriginal();
            setNewText(item.originalText);
            uiInteraction({
              item_name: 'rephrase_row_editor_restore_original',
              interaction_location:
                CONTENT_STUDIO_VIEW_NAMES.REPHRASE_ROW_EDITOR,
              interaction_details: item.originalText,
              flow_name: FLOW_NAMES.CONTENT_STUDIO.REPHRASE,
            });
          }}
        >
          {translate('Restore original')}
        </Button>
        <Button
          appearance="neutral"
          mode="tinted"
          disabled={!isEdited}
          size="small"
          onClick={() => onSubmit(true)}
        >
          {translate('Done')}
        </Button>
      </Stack>
    </Stack>
  );
}
export default SrtRowEditor;
